import { memo } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

import config from 'Config';
import FBLocaleMap from 'Lib/FBLocaleMap';
import appendLangPrefix from 'Lib/utils/appendLangPrefix';

import {
  getIntlAlternateLinks,
  getStructuredDataForWeb,
  structuredDataForOrganization,
} from './utils';

const APPLE_TOUCH_STARTUP_IMG = 'apple-touch-startup-image';

const IPHONE_5_SPLASH = '/static/iphone5_splash.png';
const IPHONE_6_SPLASH = '/static/iphone6_splash';
const IPHONE_PLUS_SPLASH = '/static/iphoneplus_splash.png';
const IPHONE_X_SPLASH = '/static/iphonex_splash.png';
const IPHONE_XR_SPLASH = '/static/iphonexr_splash.png';
const IPHONE_XS_MAX_SPLASH = '/static/iphonexsmax_splash.png';
const IPAD_SPLASH = '/static/ipad_splash.png';
const IPAD_PRO_1_SPLASH = '/static/ipadpro1_splash.png';
const IPAD_PRO_2_SPLASH = '/static/ipadpro2_splash.png';
const IPAD_PRO_3_SPLASH = '/static/ipadpro3_splash.png';

const IPHONE_5_MEDIA =
  '(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2)';
const IPHONE_6_MEDIA =
  '(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2)';
const IPHONE_PLUS_MEDIA =
  '(device-width: 621px) and (device-height: 1104px) and (-webkit-device-pixel-ratio: 3)';
const IPHONE_X_MEDIA =
  '(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3)';
const IPHONE_XR_MEDIA =
  '(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2)';
const IPHONE_XS_MAX_MEDIA =
  '(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3)';
const IPAD_MEDIA =
  '(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2)';
const IPAD_PRO_1_MEDIA =
  '(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2)';
const IPAD_PRO_2_MEDIA =
  '(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2)';
const IPAD_PRO_3_MEDIA =
  '(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2)';

const TINDER_IMAGE = `${config.siteUrl}/static/tinder.png`;

export type Props = {
  path?: string;
};

const CommonMeta = ({ path }: Props) => {
  const { formatMessage, locale } = useIntl();

  let fullUrl = config.siteUrl;
  if (path) {
    fullUrl += path;
  }

  const title = formatMessage({ id: 'Meta.title' });
  const description = formatMessage({ id: 'Meta.description' });
  const tinderName = formatMessage({ id: 'tinder' });

  return (
    <Helmet
      title={title}
      meta={[
        { name: 'charset', content: 'utf-8' },
        { name: 'description', content: description },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0, viewport-fit=cover' },
        { name: 'referrer', content: 'origin' },
        {
          name: 'copyright',
          content: `© 2016 - ${new Date().getFullYear()} Tinder, Inc., ALL RIGHTS RESERVED`,
        },

        { name: 'mobile-web-app-capable', content: 'yes' },
        { name: 'apple-mobile-web-app-capable', content: 'yes' },
        { name: 'apple-mobile-web-app-status-bar-style', content: 'white' },
        { name: 'apple-mobile-web-app-title', content: tinderName },
        { name: 'application-name', content: tinderName },
        { id: 'theme-color', name: 'theme-color', content: '#fd267a' }, // brand hot pink

        { name: 'twitter:card', content: 'summary' },
        { name: 'twitter:description', content: description },
        { name: 'twitter:site', content: '@Tinder' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:image', content: TINDER_IMAGE },

        { name: 'google-site-verification', content: config.verification.youtube },

        { property: 'fb:app_id', content: config.facebookAppSettings.appId },
        { property: 'og:description', content: description },
        { property: 'og:image', content: TINDER_IMAGE },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '1200' },
        { property: 'og:site_name', content: tinderName },
        { property: 'og:title', content: title },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: fullUrl },
        { property: 'og:locale', content: FBLocaleMap[locale] ?? 'en_US' },

        { name: 'google', content: 'notranslate' },
        // @ts-expect-error non-standard meta tag
        { 'http-equiv': 'x-ua-compatible', content: 'IE=edge' },
      ]}
      link={[
        { rel: 'apple-touch-icon', sizes: '180x180', href: '/static/apple-touch-icon.png' },

        { rel: APPLE_TOUCH_STARTUP_IMG, href: IPHONE_5_SPLASH, media: IPHONE_5_MEDIA },
        { rel: APPLE_TOUCH_STARTUP_IMG, href: IPHONE_6_SPLASH, media: IPHONE_6_MEDIA },
        { rel: APPLE_TOUCH_STARTUP_IMG, href: IPHONE_PLUS_SPLASH, media: IPHONE_PLUS_MEDIA },
        { rel: APPLE_TOUCH_STARTUP_IMG, href: IPHONE_X_SPLASH, media: IPHONE_X_MEDIA },
        { rel: APPLE_TOUCH_STARTUP_IMG, href: IPHONE_XR_SPLASH, media: IPHONE_XR_MEDIA },
        { rel: APPLE_TOUCH_STARTUP_IMG, href: IPHONE_XS_MAX_SPLASH, media: IPHONE_XS_MAX_MEDIA },
        { rel: APPLE_TOUCH_STARTUP_IMG, href: IPAD_SPLASH, media: IPAD_MEDIA },
        { rel: APPLE_TOUCH_STARTUP_IMG, href: IPAD_PRO_1_SPLASH, media: IPAD_PRO_1_MEDIA },
        { rel: APPLE_TOUCH_STARTUP_IMG, href: IPAD_PRO_2_SPLASH, media: IPAD_PRO_2_MEDIA },
        { rel: APPLE_TOUCH_STARTUP_IMG, href: IPAD_PRO_3_SPLASH, media: IPAD_PRO_3_MEDIA },

        { rel: 'icon', type: 'image/png', href: '/favicon-32x32.png', sizes: '32x32' },
        { rel: 'icon', type: 'image/png', href: '/favicon-16x16.png', sizes: '16x16' },
        { rel: 'manifest', href: `/manifest-${locale}.json` },
        { rel: 'mask-icon', href: '/static/safari-pinned-tab.svg', color: '#fd267a' },
        { rel: 'canonical', href: appendLangPrefix(fullUrl, locale) },
        ...getIntlAlternateLinks(fullUrl),
      ]}
    >
      <script type="application/ld+json">{JSON.stringify(getStructuredDataForWeb(locale))}</script>
      <script type="application/ld+json">{JSON.stringify(structuredDataForOrganization)}</script>
    </Helmet>
  );
};

export default memo(CommonMeta);
